import * as React from 'react'

import {
  Avatar,
  Box,
  Button,
  Icon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { PerformanceReviewEvaluationQuery } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'

import {
  Answer as AnswerType,
  Reviewer,
  Target,
} from 'modules/performance_process/components/insights/common/presenters'

import { AnswerCell, ScoreAnswerCell } from './AnswerCell'
import './index.styles.css'
type PerformanceQuestionSingleLabel = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        PerformanceReviewEvaluationQuery['performance']['reviewEvaluation']
      >['answeredQuestionnaires']
    >['answeredQuestionnaireWithSections']
  >['content'][number]['questions'][number]['question']
>

export interface RenderCellArgs {
  question: Question
  answer: AnswerType | undefined
  withRichTextEditor: boolean
}

type Question = PerformanceQuestionSingleLabel
type RenderCellFn = (args: RenderCellArgs) => React.ReactNode

interface Props {
  questions: Question[]
  targets: Target[]
  targetsWithReviewers: Record<number, Reviewer[]>
  answers: AnswerType[]
  collapsable?: boolean
  compact?: boolean
  renderCell: RenderCellFn
  withRichTextEditor: boolean
}

// This ensures the target background goes all the way to the end of the table when we have horizontal scroll
const calculateRowWidth = ({
  questions,
  collapsable,
  compact,
}: {
  questions: Question[]
  collapsable: boolean
  compact: boolean
}) => {
  const leftCellWidth = collapsable ? 400 : 300
  const containerPadding = 16 * 2
  const allAnswersWidth = questions.length * 300
  const cellGap = compact ? 4 : 12
  const gapBetweenAnswers = (questions.length - 1) * cellGap

  return containerPadding + allAnswersWidth + gapBetweenAnswers + leftCellWidth
}

const TargetAnswers: React.FC<{
  questions: Question[]
  answers: Set<AnswerType>
  target: Target
  reviewer: Reviewer
  withRichTextEditor: boolean
  renderCell: RenderCellFn
}> = React.memo(({ questions, answers, target, reviewer, withRichTextEditor, renderCell }) => {
  return questions.map((question) => {
    const answer = Array.from(answers).find(
      (a) =>
        a.questionId === question.uuid && a.targetId === target.id && a.reviewerId === reviewer.id
    )

    // This is a hack to prevent the same answer from being rendered multiple times
    // when we have multiple evaluations with the same reviewer and target.
    //
    // It can happen with migrated reviews and when the user replace the reviewer,
    // because we allow replacing with the same reviewer present in another evaluation.
    if (answer) answers.delete(answer)

    return (
      <Box
        key={`${target.id}-${reviewer.id}-${question.uuid}`}
        minWidth="s300"
        width="s300"
        data-testid={`target-answer-cell-${target.id}-${reviewer.id}-${question.uuid}`}
      >
        {renderCell({ question, answer, withRichTextEditor })}
      </Box>
    )
  })
})

TargetAnswers.displayName = 'TargetAnswers'

const TargetSection: React.FC<{
  target: Target
  questions: Question[]
  answers: AnswerType[]
  reviewers: Reviewer[]
  compact: boolean
  collapsable: boolean
  withRichTextEditor: boolean
  renderCell: RenderCellFn
}> = React.memo(
  ({
    target,
    questions,
    answers,
    reviewers,
    compact,
    collapsable,
    withRichTextEditor,
    renderCell,
  }) => {
    const [isCollapsed, setIsCollapsed] = React.useState(!collapsable)
    const answersSet = new Set(answers)
    const [open, toggle] = useToggle()
    const isNotSelfReview = !collapsable && !reviewers.some((reviewer) => reviewer.id == target.id)

    return (
      <Box
        key={target.id}
        borderRadius={{ all: 'abs008' }}
        padding={collapsable ? 's16' : 's0'}
        paddingLeft="s0"
        style={{ width: `${calculateRowWidth({ questions, collapsable, compact })}px` }}
        gap={compact ? 's4' : 's12'}
        background={isCollapsed ? 'white' : 'grey100'}
        data-testid={`target-section-${target.id}`}
      >
        {/* Target header */}
        <Box flexDirection="row" alignItems="stretch" gap={compact ? 's4' : 's12'}>
          <Box
            flexDirection="row"
            alignItems="center"
            gap="s12"
            minWidth={collapsable ? 's400' : 's300'}
            width={collapsable ? 's400' : 's300'}
            paddingLeft="s16"
            className={'AnswersTable_leftStickyCell'}
            background={isCollapsed ? 'white' : 'grey100'}
            data-testid={`target-name-cell-${target.id}`}
          >
            {collapsable && (
              <Button
                onClick={() => setIsCollapsed(!isCollapsed)}
                type="secondary"
                iconName={isCollapsed ? 'chevron-down' : 'chevron-up'}
              />
            )}

            {isNotSelfReview ? (
              <>
                <Avatar
                  url={buildUrl(reviewers[0]?.avatar)}
                  size="s32"
                  label={reviewers[0]?.name ? reviewers[0]?.name : ''}
                />
                <Box flexGrow minWidth="s0" width="full">
                  <Text.Normal weight="medium" size="200" ellipsis>
                    {reviewers[0]?.name}
                  </Text.Normal>
                </Box>
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s12"
                  paddingRight={compact ? 's16' : 's12'}
                  data-testid="target-avatar"
                >
                  <Icon.Medium icon="arrow-right" />
                  <Tooltip.Alt
                    open={open}
                    toggle={toggle}
                    attachment="bottom center"
                    label={target.name}
                  >
                    <Avatar url={buildUrl(target.avatar)} size="s32" label={target.name} />
                  </Tooltip.Alt>
                </Box>
              </>
            ) : (
              <Box
                data-testid="target-avatar"
                flexDirection="row"
                alignItems="center"
                gap="s12"
                paddingRight={compact ? 's16' : 's12'}
              >
                <Avatar url={buildUrl(target.avatar)} size="s32" label={target.name} />
                <Text.Normal weight="medium" size="200" ellipsis>
                  {target.name}
                </Text.Normal>
              </Box>
            )}
          </Box>

          {!collapsable &&
            reviewers.map((reviewer, index) => (
              <TargetAnswers
                key={`${target.id}-${reviewer.id}-${index}`}
                questions={questions}
                answers={answersSet}
                target={target}
                reviewer={reviewer}
                withRichTextEditor={withRichTextEditor}
                renderCell={renderCell}
              />
            ))}
        </Box>

        {/* Collapsable Content */}
        {!isCollapsed &&
          reviewers.map((reviewer, index) => (
            <Box
              key={`${target.id}-${reviewer.id}-${index}`}
              flexDirection="row"
              alignItems="stretch"
              gap={compact ? 's4' : 's12'}
            >
              <Box
                flexDirection="row"
                alignItems="center"
                minWidth={collapsable ? 's400' : 's300'}
                width={collapsable ? 's400' : 's300'}
                gap="s12"
                style={{ paddingLeft: '105px' }}
                className={'AnswersTable_leftStickyCell'}
                background={isCollapsed ? 'white' : 'grey100'}
              >
                <Avatar url={buildUrl(reviewer.avatar)} size="s32" label={reviewer.name} />
                <Box flexGrow>
                  <Text.Normal weight="medium" size="200" ellipsis>
                    {reviewer.name}
                  </Text.Normal>
                </Box>
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s12"
                  paddingRight={compact ? 's16' : 's12'}
                  data-testid="target-avatar"
                >
                  <Icon.Medium icon="arrow-right" />
                  <Avatar url={buildUrl(target.avatar)} size="s32" label={target.name} />
                </Box>
              </Box>

              <TargetAnswers
                questions={questions}
                answers={answersSet}
                target={target}
                reviewer={reviewer}
                withRichTextEditor={withRichTextEditor}
                renderCell={renderCell}
              />
            </Box>
          ))}
      </Box>
    )
  }
)

TargetSection.displayName = 'TargetSection'

const AnswersTable: React.FC<Props> & {
  AnswerCell: typeof AnswerCell
  ScoreAnswerCell: typeof ScoreAnswerCell
} = ({
  questions,
  targets,
  targetsWithReviewers,
  answers,
  compact = false,
  collapsable = false,
  renderCell,
  withRichTextEditor,
}) => {
  return (
    <Box width="full">
      <Box
        flexDirection="row"
        alignItems="stretch"
        gap={compact ? 's4' : 's12'}
        paddingBottom="s12"
        paddingRight="s16"
        className={'AnswersTable_topStickyCell'}
        background="white"
        style={{
          width: `${calculateRowWidth({
            questions: questions,
            collapsable,
            compact,
          })}px`,
        }}
      >
        <Box
          minWidth={collapsable ? 's400' : 's300'}
          width={collapsable ? 's400' : 's300'}
          background="white"
          className={'AnswersTable_leftStickyCell'}
        />
        {questions.map((question) => (
          <Box key={question.uuid} minWidth="s300" width="s300" textAlign="left">
            <Text.Normal size="200" weight="semibold" lineClamp={4}>
              {question.title}
            </Text.Normal>
          </Box>
        ))}
      </Box>

      <Box gap={compact && !collapsable ? 's4' : 's12'}>
        {targets.map((target) => {
          const reviewers = targetsWithReviewers[target.id] ?? []
          const props = {
            target,
            questions,
            answers,
            compact,
            collapsable,
            renderCell,
            withRichTextEditor,
          }

          if (collapsable) {
            return <TargetSection key={target.id} reviewers={reviewers} {...props} />
          }

          return reviewers.map((reviewer) => (
            <TargetSection key={`${target.id}-${reviewer.id}`} reviewers={[reviewer]} {...props} />
          ))
        })}
      </Box>
    </Box>
  )
}

AnswersTable.AnswerCell = AnswerCell
AnswersTable.ScoreAnswerCell = ScoreAnswerCell

export default AnswersTable
